/**
 * Receipts drawn for a Bluetooth printer.
 *
 * A receipt is a list of blocks -- centred text, a label and a value, a
 * rule, the QR -- laid out on a canvas the width of the paper. The canvas
 * shapes Arabic and lays it right to left by itself, which is the reason
 * for drawing at all (see escpos.ts). The same blocks build the invoice,
 * the credit note, the cash receipt and the day's summary.
 */
import { type Device, type VanDoc, priceLine } from './model'
import { DOTS, type Paper, rasterJob, toBitmap, trim } from './escpos'

export type Block =
  | { t: 'center'; text: string; size?: number; bold?: boolean }
  | { t: 'text'; text: string; size?: number; bold?: boolean }
  | { t: 'row'; l: string; r: string; size?: number; bold?: boolean }
  | { t: 'rule'; dashed?: boolean }
  | { t: 'qr'; src: string; size?: number }
  | { t: 'gap'; h: number }

const AR = /[؀-ۿ]/
/** Right to left only when the line is Arabic through and through: a line
 *  mixing a label and a VAT number reads left to right. */
const rtlLine = (s: string) => AR.test(s) && !/[A-Za-z0-9]/.test(s)
const FONT = "'IBM Plex Sans Arabic', 'IBM Plex Sans', Tahoma, 'Noto Sans Arabic', sans-serif"
const money = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// --- the documents -------------------------------------------------------------

function header(d: Device, title: string, titleAr: string): Block[] {
  const co = d.bootstrap.company
  return [
    { t: 'center', text: co.legal_name || co.name_en, size: 26, bold: true },
    ...(co.name_ar ? [{ t: 'center', text: co.name_ar, size: 24 } as Block] : []),
    ...(co.address_en ? [{ t: 'center', text: co.address_en, size: 18 } as Block] : []),
    ...(co.vat_number ? [{ t: 'center', text: `VAT No / الرقم الضريبي ${co.vat_number}`, size: 20,
                           bold: true } as Block] : []),
    ...(co.cr_number ? [{ t: 'center', text: `CR ${co.cr_number}`, size: 18 } as Block] : []),
    { t: 'rule', dashed: true },
    { t: 'center', text: title, size: 24, bold: true },
    { t: 'center', text: titleAr, size: 24, bold: true },
    { t: 'rule', dashed: true },
  ]
}

function when(doc: VanDoc): string {
  const time = new Date(doc.issued_at).toLocaleTimeString('en-GB',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' })
  return `${doc.doc_date} ${time}`
}

export function saleTicket(d: Device, doc: VanDoc, qr: string | null): Block[] {
  const b = d.bootstrap
  const cust = doc.customer_code ? b.customers.find((c) => c.code === doc.customer_code) : null
  const vat = doc.customer_vat || cust?.vat_number
  const ret = doc.kind === 'RETURN'
  const [en, ar] = ret ? ['Credit Note', 'إشعار دائن']
    : vat ? ['Tax Invoice', 'فاتورة ضريبية'] : ['Simplified Tax Invoice', 'فاتورة ضريبية مبسطة']
  const out: Block[] = [
    ...header(d, en, ar),
    { t: 'row', l: 'No / رقم', r: doc.doc_no, bold: true },
    { t: 'row', l: 'Date / التاريخ', r: when(doc) },
    { t: 'row', l: 'Customer / العميل', r: cust?.name_en ?? doc.customer_name ?? 'Cash customer' },
    ...(cust?.name_ar ? [{ t: 'text', text: cust.name_ar } as Block] : []),
    ...(vat ? [{ t: 'row', l: 'Buyer VAT', r: vat } as Block] : []),
    { t: 'rule' },
  ]
  for (const l of doc.lines ?? []) {
    const it = b.items.find((x) => x.code === l.item_code)
    if (!it) continue
    const p = priceLine(it, l)
    out.push({ t: 'text', text: it.name_en, bold: true })
    if (it.name_ar) out.push({ t: 'text', text: it.name_ar })
    out.push({ t: 'row', l: `${l.qty} ${l.unit} × ${money(l.price)}${Number(l.discount || 0)
      ? ` − ${money(l.discount!)}` : ''}`, r: money(p.total) })
  }
  out.push(
    { t: 'rule' },
    { t: 'row', l: 'Total excl. VAT / قبل الضريبة', r: money(doc.subtotal ?? 0) },
    { t: 'row', l: 'VAT / الضريبة', r: money(doc.vat_amount ?? 0) },
    { t: 'row', l: 'Total / الإجمالي (SAR)', r: money(doc.total ?? 0), size: 30, bold: true },
  )
  if (doc.paid || !doc.customer_code) {
    out.push({ t: 'row', l: ret ? 'Refunded / مردود نقداً' : 'Paid / مدفوع',
               r: b.van.cash_name ?? 'Cash', size: 20 })
  }
  if (doc.notes) out.push({ t: 'text', text: doc.notes, size: 20 })
  if (qr) out.push({ t: 'gap', h: 8 }, { t: 'qr', src: qr, size: 260 })
  out.push({ t: 'gap', h: 6 }, { t: 'center', text: 'Thank you · شكراً لكم', size: 20 })
  return out
}

export function collectionTicket(d: Device, doc: VanDoc): Block[] {
  const b = d.bootstrap
  const cust = b.customers.find((c) => c.code === doc.customer_code)
  return [
    ...header(d, 'Cash receipt', 'سند قبض'),
    { t: 'row', l: 'No / رقم', r: doc.doc_no, bold: true },
    { t: 'row', l: 'Date / التاريخ', r: when(doc) },
    { t: 'row', l: 'From / من', r: cust?.name_en ?? doc.customer_code ?? '' },
    ...(cust?.name_ar ? [{ t: 'text', text: cust.name_ar } as Block] : []),
    { t: 'row', l: 'Salesman / المندوب', r: b.van.salesman },
    ...(doc.notes ? [{ t: 'text', text: doc.notes, size: 20 } as Block] : []),
    { t: 'rule' },
    { t: 'row', l: 'Received / المستلم (SAR)', r: money(doc.amount ?? 0), size: 30, bold: true },
    { t: 'gap', h: 50 },
    { t: 'row', l: '____________', r: '____________' },
    { t: 'row', l: 'Received by · المستلم', r: 'Customer · العميل', size: 18 },
  ]
}

export interface DaySummary {
  date: string
  invoices: number; sales: string; cashSales: string; creditSales: string
  returns: string; refunds: string; collections: string; collectionsCount: number
  cashToHand: string
  stock: { code: string; name: string; qty: string; unit: string }[]
}

export function dayTicket(d: Device, s: DaySummary): Block[] {
  const b = d.bootstrap
  return [
    ...header(d, 'Van day summary', 'ملخص يوم السيارة'),
    { t: 'row', l: 'Van / السيارة', r: b.van.name_en },
    { t: 'row', l: 'Salesman / المندوب', r: b.van.salesman },
    { t: 'row', l: 'Date / التاريخ', r: s.date },
    { t: 'rule' },
    { t: 'row', l: `Invoices (${s.invoices}) / الفواتير`, r: money(s.sales) },
    { t: 'row', l: '  Cash / نقداً', r: money(s.cashSales) },
    { t: 'row', l: '  Credit / آجل', r: money(s.creditSales) },
    { t: 'row', l: 'Returns / المرتجعات', r: money(s.returns) },
    { t: 'row', l: `Collections (${s.collectionsCount}) / التحصيل`, r: money(s.collections) },
    { t: 'row', l: 'Cash refunds / ردود نقدية', r: money(s.refunds) },
    { t: 'rule' },
    { t: 'row', l: 'Cash to hand over / النقد المسلَّم', r: money(s.cashToHand), size: 30, bold: true },
    { t: 'rule', dashed: true },
    { t: 'center', text: 'Stock on the van / مخزون السيارة', bold: true },
    ...s.stock.map((x) => ({ t: 'row', l: `${x.code} ${x.name}`, r: `${x.qty} ${x.unit}`, size: 20 } as Block)),
    { t: 'gap', h: 50 },
    { t: 'row', l: '____________', r: '____________' },
    { t: 'row', l: 'Salesman · المندوب', r: 'Cashier · أمين الصندوق', size: 18 },
  ]
}

// --- drawing -------------------------------------------------------------------

function font(size = 22, bold = false) { return `${bold ? 700 : 400} ${size}px ${FONT}` }

function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let cur = ''
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w
    if (ctx.measureText(next).width <= max || !cur) cur = next
    else { lines.push(cur); cur = w }
  }
  if (cur) lines.push(cur)
  return lines
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/** Draw the blocks on a canvas `width` dots wide. */
export async function draw(blocks: Block[], width: number): Promise<HTMLCanvasElement> {
  const pad = 6
  const inner = width - pad * 2
  const images = new Map<string, HTMLImageElement>()
  for (const b of blocks) if (b.t === 'qr' && !images.has(b.src)) images.set(b.src, await loadImage(b.src))
  try { await (document as Document & { fonts?: FontFaceSet }).fonts?.ready } catch { /* none */ }

  const probe = document.createElement('canvas').getContext('2d')!
  const plan: { b: Block; h: number; lines?: string[] }[] = []
  for (const b of blocks) {
    const size = 'size' in b && b.size ? b.size : 22
    probe.font = font(size, 'bold' in b && !!b.bold)
    const lh = Math.round(size * 1.3)
    if (b.t === 'center' || b.t === 'text') {
      const lines = wrap(probe, b.text, inner)
      plan.push({ b, h: lines.length * lh, lines })
    } else if (b.t === 'row') {
      const rw = probe.measureText(b.r).width
      const lines = wrap(probe, b.l, Math.max(inner - rw - 12, inner * 0.4))
      plan.push({ b, h: lines.length * lh, lines })
    } else if (b.t === 'rule') plan.push({ b, h: 14 })
    else if (b.t === 'qr') plan.push({ b, h: (b.size ?? 240) + 4 })
    else plan.push({ b, h: b.h })
  }
  const height = plan.reduce((n, p) => n + p.h, 0) + pad * 2
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, width, height)
  ctx.fillStyle = '#000'
  ctx.strokeStyle = '#000'
  ctx.textBaseline = 'top'
  let y = pad
  for (const { b, h, lines } of plan) {
    const size = 'size' in b && b.size ? b.size : 22
    const lh = Math.round(size * 1.3)
    ctx.font = font(size, 'bold' in b && !!b.bold)
    if (b.t === 'center') {
      for (const [i, line] of lines!.entries()) {
        ctx.direction = rtlLine(line) ? 'rtl' : 'ltr'
        ctx.textAlign = 'center'
        ctx.fillText(line, width / 2, y + i * lh)
      }
    } else if (b.t === 'text') {
      for (const [i, line] of lines!.entries()) {
        const rtl = rtlLine(line)
        ctx.direction = rtl ? 'rtl' : 'ltr'
        ctx.textAlign = rtl ? 'right' : 'left'
        ctx.fillText(line, rtl ? width - pad : pad, y + i * lh)
      }
    } else if (b.t === 'row') {
      for (const [i, line] of lines!.entries()) {
        ctx.direction = 'ltr'
        ctx.textAlign = 'left'
        ctx.fillText(line, pad, y + i * lh)
      }
      // Anchored at the right edge either way: "start" of right-to-left
      // text and "end" of left-to-right text are both the right margin.
      const rtl = rtlLine(b.r)
      ctx.direction = rtl ? 'rtl' : 'ltr'
      ctx.textAlign = rtl ? 'start' : 'end'
      ctx.fillText(b.r, width - pad, y + (lines!.length - 1) * lh)
    } else if (b.t === 'rule') {
      ctx.lineWidth = 2
      ctx.setLineDash(b.dashed ? [8, 6] : [])
      ctx.beginPath(); ctx.moveTo(pad, y + 7); ctx.lineTo(width - pad, y + 7); ctx.stroke()
      ctx.setLineDash([])
    } else if (b.t === 'qr') {
      const s = b.size ?? 240
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(images.get(b.src)!, (width - s) / 2, y, s, s)
    }
    y += h
  }
  return canvas
}

/** The bytes to send: the blocks drawn, made 1-bit, wrapped in ESC/POS. */
export async function printerBytes(blocks: Block[], paper: Paper = 80): Promise<Uint8Array> {
  const canvas = await draw(blocks, DOTS[paper])
  const ctx = canvas.getContext('2d')!
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
  return rasterJob(trim(toBitmap(img.data, canvas.width, canvas.height)))
}
